import { Button, CircularProgress, Stack, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useCallback } from 'react'
import { CreateOrderPageViewStore } from '../../stores/CreateOrderPageViewStore'

interface IProps {
  createOrderPageViewStore: CreateOrderPageViewStore
  pageTitle?: string
  saveDisabled?: boolean
  onSubmit?: (createdOrders?: { [key: string]: number }) => void
  onBack?: () => void
}

export const CreateOrderPageHeader: FC<IProps> = observer(
  ({ createOrderPageViewStore, pageTitle, onSubmit, onBack }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { appViewStore } = usePlanningStore()

    const createOrder = useCallback(async () => {
      try {
        appViewStore.showLoader()
        const createdOrders = await createOrderPageViewStore.submit()
        onSubmit?.(createdOrders)

        appViewStore.setShowAlert(
          'success',
          t('orderCreatedSuccessfully', 'Order Created Successfully'),
        )
      } catch (error) {
        appViewStore.setShowAlert('error', t('orderCreationFailed', 'Order Creation Failed'))
      } finally {
        appViewStore.hideLoader()
      }
    }, [appViewStore, createOrderPageViewStore, onSubmit, t])

    return (
      <Stack
        paddingX={theme.customSpacing.l}
        paddingY={theme.customSpacing.s}
        alignItems={'center'}
        direction={'row'}
        sx={{ placeContent: 'space-between' }}
        borderBottom={`1px solid ${theme.palette.divider}`}
      >
        <Stack alignItems={'flex-start'}>
          <Typography variant='h3' align='center'>
            {pageTitle ?? t('orderDetails', 'Order details')}
          </Typography>
        </Stack>

        <Stack gap={theme.customSpacing.m} direction='row' alignItems='center'>
          <Button variant='text' onClick={onBack} color='inherit' data-cy='page-header-cancel-btn'>
            {t('cancel', 'Cancel')}
          </Button>

          <Button
            disabled={appViewStore.isLoading}
            variant='contained'
            color='primary'
            onClick={async () => {
              if (createOrderPageViewStore.stepperFormViewStore.isLastStep) {
                await createOrder()
                onBack?.()
              } else {
                await createOrderPageViewStore.validateAndChangeStep()
              }
            }}
            data-cy='page-header-submit-btn'
            type='button'
            startIcon={appViewStore.isLoading && <CircularProgress size='1rem' />}
          >
            {createOrderPageViewStore.stepperFormViewStore.isLastStep
              ? t('submit', 'Submit')
              : t('next', 'Next')}
          </Button>
        </Stack>
      </Stack>
    )
  },
)
